import {
  SetStateAction,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState
} from "react";
import { Autocomplete, TextField } from "@mui/material";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";

import BlastMasterIdsLookupDTO from "../../../DTOs/Production/BlastMasterIdsLookupDTO";
import { DiggingLocationDTO } from "../../../DTOs/Production/DiggingLocationDTO";
import { PitStageDTO } from "../../../DTOs/PitStageDTO";
import { diggingAjax } from "../../../ajax/DiggingAjax";
import { diggingPatternMutations } from "../../../offline/diggingPatternMutations";
import { referencesAjax } from "../../../ajax/ReferencesAjax";
import { DataTable, TableRow } from "../../../components/DataTable/DataTable";
import { cellValue } from "../../../components/DataTable/cellValue";
import { useRequiredCells } from "../../../components/DataTable/useRequiredCells";
import { DataTableFilteredRequest, useDataTable } from "../../../components/DataTable/TableHook";
import { useOptions } from "../../../components/Hooks/useOptions";
import useShiftTimes from "../../../components/Hooks/useShiftTimes";
import { TripleShift } from "../../../types/Shift";
import ShiftTableNoResults from "../../../components/ShiftTableNoResults/ShiftTableNoResults";
import { TimePicker } from "../../../components/TimePicker/TimePicker";
import { useCanWrite, useIsReadOnly } from "../../../utilities/helpers/roleUtilities";
import OpenMisfireWarning from "../../../components/DrillBlast/OpenMisfireWarning";

type PitStageOption = { label: string; value: number; pitId: number };

interface InsertRowState {
  pitStageId: number;
  patternBlastId: number;
  timeStart: DateTime | null;
}

interface InsertRowProps {
  state: InsertRowState;
  setState: React.Dispatch<React.SetStateAction<InsertRowState>>;
  onInsert: () => Promise<boolean>;
  isPitStagesLoading: boolean
  pitStageOptions: PitStageOption[];
  getBlastOptions: (pitStageId: number) => BlastMasterIdsLookupDTO[]
  shiftStartTime: DateTime;
  shiftEndTime: DateTime;
  isReadOnly: boolean
}

const InsertInputsRow = ({
  state,
  setState,
  onInsert,
  isPitStagesLoading,
  pitStageOptions,
  getBlastOptions,
  shiftStartTime,
  shiftEndTime,
  isReadOnly
}: InsertRowProps) => {
  const { t } = useTranslation("production");
  const availableBlastMasterOptions = useMemo(() => getBlastOptions(state.pitStageId), [state.pitStageId, getBlastOptions]);

  return (
    <ul className="flex gap-2 border-b border-gray-300">
      <li className="basis-[7%] flex items-center justify-center text-[0.9rem] w-full" />

      <li className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        <Autocomplete<PitStageOption, false, false, false>
          disabled={isReadOnly}
          classes={{ root: "w-full" }}
          size="small"
          options={pitStageOptions}
          value={pitStageOptions.find(o => o.value === state.pitStageId) ?? null}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          onChange={(_, opt) => setState(s => ({ ...s, pitStageId: opt?.value ?? 0 }))}
          renderInput={(params) => <TextField {...params}
            placeholder={t("fields.pitStage")}
            size="small"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isPitStagesLoading ?
                      <FontAwesomeIcon icon={faSpinner} spin /> : null}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }} />}
        />
      </li>

      <li className="basis-[20%] flex items-center justify-center text-[0.9rem] w-full">
        <Autocomplete<BlastMasterIdsLookupDTO, false, false, false>
          disabled={isReadOnly}
          classes={{ root: "w-full" }}
          size="small"
          options={availableBlastMasterOptions}
          value={availableBlastMasterOptions.find(o => o.blastMasterId === state.patternBlastId) ?? null}
          getOptionLabel={o => o.blastMasterId.toString()}
          isOptionEqualToValue={(opt, val) => opt.blastMasterId === val.blastMasterId}
          onChange={(_, opt) => setState(s => ({ ...s, patternBlastId: opt?.blastMasterId ?? 0 }))}
          noOptionsText={t("locationsTable.noBlastOptions")}
          renderInput={(params) => <TextField {...params}
            placeholder={t("columns.blastMasterId")}
            size="small"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isPitStagesLoading ?
                      <FontAwesomeIcon icon={faSpinner} spin /> : null}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }} />}
        />
      </li>

      <li className="basis-[10%] flex items-center justify-center text-[0.9rem] w-full" />

      <li className="basis-[10%] flex items-center justify-center text-[0.9rem] w-full">
        <TimePicker
          disabled={isReadOnly}
          initialDate={state.timeStart}
          onChange={e => setState(s => ({ ...s, timeStart: e! }))}
          minValue={shiftStartTime}
          maxValue={shiftEndTime}
        />
      </li>

      <li className="basis-[18%] flex items-center justify-center text-[0.9rem] w-full">
        <button
          onClick={onInsert}
          disabled={isReadOnly}
          className="bg-theme-info hover:bg-theme-info-hover disabled:opacity-50 transition ease-in-out text-white font-bold py-2 px-4 rounded w-full whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
        >
          {t("actions.add")}
        </button>
      </li>
    </ul>
  )
}

interface LocationsTableProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  diggerId: number;
  selectedLocation: DiggingLocationDTO | null;
  setSelectedLocation: React.Dispatch<SetStateAction<DiggingLocationDTO | null>>;
  highlightedLocationRowKey: string | null;
  pulseRowKey: string | null;
  getBlastOptions: (pitStageId: number) => BlastMasterIdsLookupDTO[];
  onLocationsChanged?: (locations: DiggingLocationDTO[]) => void;
}

export const LocationsTable: React.FC<LocationsTableProps> = ({
  selectedDate,
  selectedShift,
  diggerId,
  selectedLocation,
  setSelectedLocation,
  highlightedLocationRowKey,
  pulseRowKey,
  getBlastOptions,
  onLocationsChanged
}) => {
  const { t } = useTranslation("production");
  const { enqueueSnackbar } = useSnackbar();
  const { requireFilled } = useRequiredCells();
  const { shiftStartTime, shiftEndTime } = useShiftTimes();
  const isReadOnly = useIsReadOnly();
  const canInsert = useCanWrite("Digging.InsertDiggingLocation");
  const canUpdate = useCanWrite("Digging.UpdateDiggingLocation");
  const canDelete = useCanWrite("Digging.DeleteDiggingPattern");
  const {
    options: allPitStages,
    isLoading: isPitStagesLoading
  } = useOptions<PitStageDTO, PitStageDTO>(
    ['references', 'pitStages'],
    () => referencesAjax.getPitStages({ pageNumber: 1, pageSize: 100 } as DataTableFilteredRequest),
    x => x,
  );

  const [insertState, setInsertState] = useState<InsertRowState>({
    pitStageId: 0,
    patternBlastId: 0,
    timeStart: null
  });

  const pitStageOptions = useMemo<PitStageOption[]>(() =>
    allPitStages
      .filter(s => s.startDate <= selectedDate && (s.endDate == null || s.endDate >= selectedDate))
      .map(s => ({ label: s.name ?? '', value: s.id ?? 0, pitId: s.pitId })),
    [allPitStages, selectedDate]
  );

  const getRows = useCallback(() => diggingAjax.getDiggerLocations(selectedDate, selectedShift, diggerId), [selectedDate, selectedShift, diggerId]);

  const {
    rows,
    fetchedRows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    refresh,
    appendRowSource,
    updateRowSource,
    removeRowSource
  } = useDataTable<DiggingLocationDTO>({
    fetchFn: getRows,
    queryKey: ['chatree', 'diggerLocations', selectedDate.toISODate(), selectedShift, diggerId],
    initialPage: 1,
    pageSize: 5,
  });

  const syncWithFetchedLocations = useEffectEvent((data: DiggingLocationDTO[]) => {
    onLocationsChanged?.(data);
    if (highlightedLocationRowKey) {
      setSelectedLocation(data.find(row => row.diggingPatternId === Number(highlightedLocationRowKey)) || null);
    } else {
      const isValid = selectedLocation != null && data.some(row => row.diggingPatternId === selectedLocation.diggingPatternId);
      if (!isValid) {
        setSelectedLocation(data[0] ?? null);
      }
    }
  });

  useEffect(() => {
    if (fetchedRows) syncWithFetchedLocations(fetchedRows);
  }, [fetchedRows]);

  const handleUpdateRow = useCallback(
    async (loc: DiggingLocationDTO) => {
      if (!requireFilled([[t("fields.pitStage"), loc.pitStageId || null], [t("columns.blastMasterId"), loc.blastMasterId || null]])) return false;

      const resp = await diggingPatternMutations.updateDiggingLocation(loc);

      if (!resp.success || !resp.data) {
        resp.errors?.forEach((e, i) =>
          enqueueSnackbar(e.description, { variant: "error", autoHideDuration: 4000 + i * 1000 })
        );
        return false;
      }

      enqueueSnackbar(t("locationsTable.patternUpdated"), { variant: 'success', autoHideDuration: 3000 })

      updateRowSource(row => row.diggingPatternId === resp.data!.diggingPatternId, resp.data!);
      refresh();
      setSelectedLocation(resp.data);
      return true;
    },
    [setSelectedLocation, enqueueSnackbar, updateRowSource, refresh, requireFilled, t]
  );

  const handleDeleteRow = useCallback(
    async (diggingPatternId: number, rowVersion?: string) => {
      const response = await diggingPatternMutations.deleteDiggingPattern(diggingPatternId, rowVersion);

      if (!response.success && !response.data) {
        response.errors?.forEach((error, index) => {
          enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
        })

        return false;
      }

      if (diggingPatternId === selectedLocation?.diggingPatternId) {
        setSelectedLocation(null);
      }

      removeRowSource(row => row.diggingPatternId === diggingPatternId);
      refresh();
      enqueueSnackbar(t("locationsTable.patternDeleted"), { variant: 'success', autoHideDuration: 3000 })

      return true;
    },
    [selectedLocation?.diggingPatternId, setSelectedLocation, enqueueSnackbar, removeRowSource, refresh, t]
  );

  const handleInsertRow = useCallback(async () => {
    let isValid = true;

    if (insertState.pitStageId === 0) {
      enqueueSnackbar(t("locationsTable.pitStageRequired"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.patternBlastId === 0) {
      enqueueSnackbar(t("locationsTable.blastMasterRequired"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.timeStart == null) {
      enqueueSnackbar(t("validation.startTimeRequired"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const selectedStage = pitStageOptions.find(p => p.value === insertState.pitStageId);
    const payload = {
      diggingPatternId: 0,
      pit: '',
      pitId: selectedStage?.pitId ?? 0,
      patternBlast: "",
      patternBlastId: 0,
      pitStage: selectedStage?.label ?? '',
      pitStageId: insertState.pitStageId,
      startTime: insertState.timeStart,
      workingCondition: 1,
      blastMasterId: insertState.patternBlastId
    } as DiggingLocationDTO;

    const response = await diggingPatternMutations.insertDiggingLocation(
      selectedDate,
      selectedShift,
      diggerId,
      payload
    );

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    setInsertState({
      pitStageId: 0,
      patternBlastId: 0,
      timeStart: null
    })

    enqueueSnackbar(t("locationsTable.locationAdded"), { variant: 'success', autoHideDuration: 3000 })

    appendRowSource(response.data!);
    refresh();
    setSelectedLocation(response.data!)
    return true;
  }, [
    insertState.pitStageId,
    insertState.patternBlastId,
    insertState.timeStart,
    pitStageOptions,
    selectedDate,
    selectedShift,
    diggerId,
    enqueueSnackbar,
    setSelectedLocation,
    appendRowSource,
    refresh,
    t
  ]);

  const translateFn = useCallback((r: DiggingLocationDTO): TableRow => (
    {
      id: r.diggingPatternId,
      rowContainerTailwindClasses: `${r.diggingPatternId === selectedLocation?.diggingPatternId ? "relative z-[10]" : ""}`,
      rowTailwindClasses: `py-0.5 ${r.diggingPatternId === selectedLocation?.diggingPatternId ? "outline outline-theme-success rounded bg-theme-success/20" : ""} ${pulseRowKey === `${r.diggingPatternId}` ? "glow-attention" : ""}`,
      values: [
        {
          name: "",
          type: "custom",
          value: "custom",
          tailwindClasses: "basis-[7%] flex items-center justify-center text-[0.9rem] w-full",
          render: () => (
            <span
              style={{
                backgroundColor: r.diggingPatternId === selectedLocation?.diggingPatternId ? "#1b931b" : "inherit"
              }}
              className="px-1 text-white font-bold w-full h-full flex items-center justify-center"
            >
              {r.diggingPatternId === selectedLocation?.diggingPatternId
                ? t("locationsTable.selected")
                : ""}
            </span>
          )
        },
        {
          name: "Pit Stage",
          type: "integer",
          value: r.pitStageId,
          tailwindClasses: "basis-[15%] flex items-center justify-center text-[0.9rem] w-full",
          render: (_val, _row, helpers) => (
            <Autocomplete<PitStageOption, false, false, false>
              disabled={isReadOnly || !canUpdate}
              classes={{ root: "w-full" }}
              size="small"
              options={pitStageOptions}
              value={pitStageOptions.find(o => o.value === helpers.draft.get()) ?? null}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(opt, v) => opt.value === v.value}
              onChange={(_, opt) => helpers.draft.set(opt?.value ?? 0)}
              renderInput={(params) => <TextField {...params}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {isPitStagesLoading ?
                          <FontAwesomeIcon icon={faSpinner} spin /> : null}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }} />}
            />
          )
        },
        {
          name: "Blast Master Id",
          type: "integer",
          value: r.blastMasterId ?? r.patternBlastId,
          tailwindClasses: "basis-[20%] flex items-center justify-center text-[0.9rem] w-full",
          render: (_val, row, helpers) => {
            const selectedPitStageId = cellValue(row, "Pit Stage", "integer");
            const opts = getBlastOptions(selectedPitStageId ?? 0);
            return (
              <Autocomplete<BlastMasterIdsLookupDTO, false, false, false>
                disabled={isReadOnly || !canUpdate}
                classes={{ root: "w-full" }}
                size="small"
                options={opts}
                value={opts.find(o => o.blastMasterId === helpers.draft.get()) ?? null}
                getOptionLabel={o => o.blastMasterId.toString()}
                isOptionEqualToValue={(opt, v) => opt.blastMasterId === v.blastMasterId}
                noOptionsText={t("locationsTable.noBlastOptions")}
                onChange={(_, opt) => helpers.draft.set(opt?.blastMasterId ?? 0)}
                renderInput={(params) => <TextField {...params}
                  placeholder={t("locationsTable.selectBlastMasterId")}
                  size="small"
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {(isPitStagesLoading) ?
                            <FontAwesomeIcon icon={faSpinner} spin /> : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    },
                  }} />}
              />
            );
          }
        },
        {
          name: "Pit",
          type: "string",
          value: r.pit,
          tailwindClasses: "basis-[10%] flex items-center justify-center text-[0.9rem] w-full"
        },
        {
          name: "Start Time",
          type: "date",
          value: r.startTime.toLocal(),
          tailwindClasses: "basis-[10%] flex items-center justify-center text-[0.9rem] w-full",
          render: (val, _row, helpers) => (
            <TimePicker
              disabled={isReadOnly || !canUpdate}
              initialDate={val.value}
              onChange={e => helpers.draft.set(e!)}
              minValue={shiftStartTime}
              maxValue={shiftEndTime}
            />
          )
        },
        {
          name: "Actions",
          type: "custom",
          value: "custom",
          tailwindClasses: "basis-[18%] flex gap-2 items-center justify-center text-[0.9rem] w-full",
          render: (_, row) => {
            const newPitStageId = cellValue(row, "Pit Stage", "integer") ?? 0;
            const newPitId = pitStageOptions.find(o => o.value === newPitStageId)?.pitId ?? 0;

            const payload: DiggingLocationDTO = {
              ...r,
              pitStageId: newPitStageId,
              pitId: newPitId,
              patternBlastId: 0,
              startTime: cellValue(row, "Start Time", "date") ?? r.startTime,
              blastMasterId: cellValue(row, "Blast Master Id", "integer") ?? 0,
              workingCondition: r.workingCondition
            };

            return (
              <div className="w-full flex gap-1 justify-between">
                <button
                  onClick={() => {
                    setSelectedLocation(r);
                  }}
                  className="bg-theme-warning hover:bg-theme-warning-hover cursor-pointer transition ease-in-out text-white font-bold py-2 px-4 rounded whitespace-nowrap"
                >
                  {t("actions.select")}
                </button>
                <button
                  disabled={isReadOnly || !canUpdate}
                  onClick={async () => {
                    await handleUpdateRow(payload);
                  }}
                  className="bg-theme-success hover:bg-theme-success-hover disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed transition ease-in-out text-white font-bold py-2 px-4 rounded whitespace-nowrap"
                >
                  {t("actions.save")}
                </button>
                <button
                  disabled={isReadOnly || !canDelete}
                  onClick={async () => {
                    await handleDeleteRow(r.diggingPatternId, r.rowVersion);
                  }}
                  className="bg-theme-danger hover:bg-theme-danger-hover disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed transition ease-in-out text-white font-bold py-2 px-4 rounded whitespace-nowrap"
                >
                  {t("actions.delete")}
                </button>
              </div>
            );
          }
        }
      ]
    }), [
    selectedLocation?.diggingPatternId,
    pulseRowKey,
    isReadOnly,
    canUpdate,
    canDelete,
    pitStageOptions,
    isPitStagesLoading,
    getBlastOptions,
    shiftStartTime,
    shiftEndTime,
    setSelectedLocation,
    handleUpdateRow,
    handleDeleteRow,
    t
  ])

  const displayData = useMemo(() => rows.map(translateFn), [rows, translateFn]);

  return (
    <div className="mt-4 compact:mt-0 w-full container mx-auto">
      <h2 className="text-2xl font-bold mb-2 text-center text-theme-text">{t("fields.location")}</h2>
      <OpenMisfireWarning patternIds={rows.map(r => r.patternBlastId)} />
      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        error={error}
        pageNumber={pageNumber}
        setPage={setPage}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1320}
        fillHeight
        minTableFloorCssValue='8rem'
        disableContainer={true}
        noResultsContent={<ShiftTableNoResults
          empty={t("locationsTable.empty")}
          entity={t("locationsTable.entity")}
          howToAdd={<Trans t={t} i18nKey="locationsTable.howToAdd" components={{ bold: <span className="font-bold" /> }} />}
          addWrite="Digging.InsertDiggingLocation"
        />}
        insertRowRenderer={() => (
          <InsertInputsRow
            state={insertState}
            setState={setInsertState}
            onInsert={handleInsertRow}
            isPitStagesLoading={isPitStagesLoading}
            pitStageOptions={pitStageOptions}
            getBlastOptions={getBlastOptions}
            shiftStartTime={shiftStartTime}
            shiftEndTime={shiftEndTime}
            isReadOnly={isReadOnly || !canInsert}
          />
        )}
        overrideHeaderCells={[
          {
            name: "",
            tailwindClasses: "basis-[7%] flex items-center justify-center text-[0.9rem] w-full"
          },
          {
            name: "Pit Stage",
            label: t("fields.pitStage"),
            tailwindClasses: "basis-[15%] flex items-center justify-center text-[0.9rem] w-full"
          },
          {
            name: "Blast Master Id",
            label: t("columns.blastMasterId"),
            tailwindClasses: "basis-[20%] flex items-center justify-center text-[0.9rem] w-full"
          },
          {
            name: "Pit",
            label: t("columns.pit"),
            tailwindClasses: "basis-[10%] flex items-center justify-center text-[0.9rem] w-full"
          },
          {
            name: "Start Time",
            label: t("fields.startTime"),
            tailwindClasses: "basis-[10%] flex items-center justify-center text-[0.9rem] w-full"
          },
          {
            name: "Actions",
            label: t("fields.actions"),
            tailwindClasses: "basis-[18%] flex gap-2 items-center justify-center text-[0.9rem] w-full"
          }
        ]}
      />
    </div>
  );
};
